import dayjs from 'dayjs'
import { Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { Approval } from '@/types'
import { EllipsisCell } from '@/components/table/EllipsisCell'
import {
  getColumnSearchProps,
  getColumnEnumFilterProps,
  textMatchesColumnFilter,
} from '@/components/table/columnSearchProps'
import { DOC_TYPE_FILTER_OPTIONS, STATUS_FILTER_OPTIONS } from '@/components/table/approvalTableConstants'

function nz(v: unknown): string {
  if (v == null || String(v).trim() === '') return '—'
  return String(v)
}



/** All persisted approval columns (URLs + remarks truncated visually; SAP JSON preview). Horizontal scroll recommended. */
export const FULL_APPROVAL_DATA_COLUMNS: ColumnsType<Approval> = [
  {
    title: 'ID',
    dataIndex: 'id',
    key: 'id',
    width: 70,
    ellipsis: true,
    sorter: (a: Approval, b: Approval) => Number(a.id) - Number(b.id),
    ...getColumnSearchProps<Approval>('ID', (value, record) => textMatchesColumnFilter(value, record.id)),
  },
  {
    title: 'Document No',
    dataIndex: 'documentNo',
    key: 'documentNo',
    width: 120,
    ellipsis: true,
    sorter: (a: Approval, b: Approval) => String(a.documentNo).localeCompare(String(b.documentNo)),
    ...getColumnSearchProps<Approval>('Document No', (value, record) =>
      textMatchesColumnFilter(value, record.documentNo)
    ),
  },
  {
    title: 'Type',
    dataIndex: 'type',
    key: 'type',
    width: 94,
    render: (type: string) => (
      <Tag
        color={
          type === 'ALT'
            ? 'blue'
            : type === 'DHR'
              ? 'green'
              : type === 'GRN'
                ? 'orange'
                : type === 'REL_OUT'
                  ? 'purple'
                  : 'cyan'
        }
      >
        {type}
      </Tag>
    ),
    ...getColumnEnumFilterProps<Approval>('Type', DOC_TYPE_FILTER_OPTIONS, (r) => r.type),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: 112,
    render: (status: string) => {
      const color = status === 'Approved' ? 'green' : status === 'Rejected' ? 'red' : 'orange'
      return <Tag color={color}>{status}</Tag>
    },
    ...getColumnEnumFilterProps<Approval>('Status', STATUS_FILTER_OPTIONS, (r) => r.status),
  },
  {
    title: 'Year',
    dataIndex: 'year',
    key: 'year',
    width: 86,
    ellipsis: true,
    render: (_: unknown, r: Approval) => nz(r.year),
    ...getColumnSearchProps<Approval>('Year', (value, record) => textMatchesColumnFilter(value, record.year)),
  },
  {
    title: 'Posting date',
    dataIndex: 'postingDate',
    key: 'postingDate',
    width: 120,
    ellipsis: true,
    render: (_: unknown, r: Approval) => nz(r.postingDate),
    ...getColumnSearchProps<Approval>('Posting', (value, record) =>
      textMatchesColumnFilter(value, record.postingDate)
    ),
  },
  {
    title: 'App',
    dataIndex: 'app',
    key: 'app',
    width: 88,
    ellipsis: true,
    render: (_: unknown, r: Approval) => nz(r.app),
    ...getColumnSearchProps<Approval>('App', (value, record) => textMatchesColumnFilter(value, record.app)),
  },
  {
    title: 'Material',
    dataIndex: 'materialCodeNo',
    key: 'materialCodeNo',
    width: 120,
    ellipsis: true,
    render: (_: unknown, r: Approval) => nz(r.materialCodeNo),
    ...getColumnSearchProps<Approval>('Material', (value, record) =>
      textMatchesColumnFilter(value, record.materialCodeNo)
    ),
  },
  {
    title: 'Cust. code',
    dataIndex: 'customerCode',
    key: 'customerCode',
    width: 110,
    ellipsis: true,
    render: (_: unknown, r: Approval) => nz(r.customerCode),
    ...getColumnSearchProps<Approval>('Cust code', (value, record) =>
      textMatchesColumnFilter(value, record.customerCode)
    ),
  },
  {
    title: 'Customer',
    dataIndex: 'customerName',
    key: 'customerName',
    width: 180,
    ellipsis: true,
    render: (value: string | null | undefined) => <EllipsisCell>{nz(value)}</EllipsisCell>,
    ...getColumnSearchProps<Approval>('Customer', (value, record) =>
      textMatchesColumnFilter(value, record.customerName)
    ),
  },
  {
    title: 'From',
    dataIndex: 'fromLocation',
    key: 'fromLocation',
    width: 140,
    ellipsis: true,
    render: (value: string | null | undefined) => <EllipsisCell>{value}</EllipsisCell>,
    ...getColumnSearchProps<Approval>('From', (value, record) => textMatchesColumnFilter(value, record.fromLocation)),
  },
  {
    title: 'To',
    dataIndex: 'toLocation',
    key: 'toLocation',
    width: 140,
    ellipsis: true,
    render: (value: string | null | undefined) => <EllipsisCell>{value}</EllipsisCell>,
    ...getColumnSearchProps<Approval>('To', (value, record) => textMatchesColumnFilter(value, record.toLocation)),
  },
  {
    title: 'Qty',
    dataIndex: 'quantity',
    key: 'quantity',
    width: 88,
    ellipsis: true,
    render: (_: unknown, r: Approval) => nz(r.quantity),
    ...getColumnSearchProps<Approval>('Qty', (value, record) => textMatchesColumnFilter(value, record.quantity)),
  },
  {
    title: 'Actual',
    dataIndex: 'actual_qty',
    key: 'actual_qty',
    width: 88,
    ellipsis: true,
    render: (_: unknown, r: Approval) => nz(r.actual_qty),
    ...getColumnSearchProps<Approval>('Actual', (value, record) =>
      textMatchesColumnFilter(value, record.actual_qty)
    ),
  },
  {
    title: 'Excess',
    dataIndex: 'excess_qty',
    key: 'excess_qty',
    width: 88,
    ellipsis: true,
    render: (_: unknown, r: Approval) => nz(r.excess_qty),
    ...getColumnSearchProps<Approval>('Excess', (value, record) =>
      textMatchesColumnFilter(value, record.excess_qty)
    ),
  },
  {
    title: 'Short',
    dataIndex: 'short_qty',
    key: 'short_qty',
    width: 86,
    ellipsis: true,
    render: (_: unknown, r: Approval) => nz(r.short_qty),
    ...getColumnSearchProps<Approval>('Short', (value, record) => textMatchesColumnFilter(value, record.short_qty)),
  },
  {
    title: 'Vehicle',
    dataIndex: 'vehicleNo',
    key: 'vehicleNo',
    width: 110,
    ellipsis: true,
    render: (_: unknown, r: Approval) => nz(r.vehicleNo),
    ...getColumnSearchProps<Approval>('Vehicle', (value, record) =>
      textMatchesColumnFilter(value, record.vehicleNo)
    ),
  },
  {
    title: 'LR No',
    dataIndex: 'lrNo',
    key: 'lrNo',
    width: 100,
    ellipsis: true,
    render: (_: unknown, r: Approval) => nz(r.lrNo),
    ...getColumnSearchProps<Approval>('LR', (value, record) => textMatchesColumnFilter(value, record.lrNo)),
  },
  {
    title: 'Tpt code',
    dataIndex: 'transporterCode',
    key: 'transporterCode',
    width: 100,
    ellipsis: true,
    render: (_: unknown, r: Approval) => nz(r.transporterCode),
    ...getColumnSearchProps<Approval>('Tpt code', (value, record) =>
      textMatchesColumnFilter(value, record.transporterCode)
    ),
  },
  {
    title: 'Transporter',
    dataIndex: 'transporterName',
    key: 'transporterName',
    width: 160,
    ellipsis: true,
    render: (_: unknown, r: Approval) => <EllipsisCell>{nz(r.transporterName)}</EllipsisCell>,
    ...getColumnSearchProps<Approval>('Transporter', (value, record) =>
      textMatchesColumnFilter(value, record.transporterName)
    ),
  },
  {
    title: 'Approver remark',
    dataIndex: 'approval_remark',
    key: 'approval_remark',
    width: 160,
    ellipsis: true,
    render: (_: unknown, r: Approval) => nz(r.approval_remark),
    ...getColumnSearchProps<Approval>('Appr rm', (value, record) =>
      textMatchesColumnFilter(value, record.approval_remark)
    ),
  },
  {
    title: 'Reject remark',
    dataIndex: 'rejection_remark',
    key: 'rejection_remark',
    width: 160,
    ellipsis: true,
    render: (_: unknown, r: Approval) => nz(r.rejection_remark),
    ...getColumnSearchProps<Approval>('Rej rm', (value, record) =>
      textMatchesColumnFilter(value, record.rejection_remark)
    ),
  },
  {
    title: 'User ID',
    dataIndex: 'userId',
    key: 'userId',
    width: 92,
    ellipsis: true,
    render: (_: unknown, r: Approval) => nz(r.userId),
    ...getColumnSearchProps<Approval>('UID', (value, record) => textMatchesColumnFilter(value, record.userId)),
  },
  {
    title: 'User ref',
    dataIndex: 'userUserId',
    key: 'userUserId',
    width: 100,
    ellipsis: true,
    render: (_: unknown, r: Approval) => nz(r.userUserId),
    ...getColumnSearchProps<Approval>('UserRef', (value, record) =>
      textMatchesColumnFilter(value, record.userUserId)
    ),
  },
  {
    title: 'Appr ID',
    dataIndex: 'approverId',
    key: 'approverId',
    width: 90,
    ellipsis: true,
    render: (_: unknown, r: Approval) => nz(r.approverId),
    ...getColumnSearchProps<Approval>('ApprID', (value, record) =>
      textMatchesColumnFilter(value, record.approverId)
    ),
  },
  {
    title: 'Appr ref',
    dataIndex: 'approverUserId',
    key: 'approverUserId',
    width: 96,
    ellipsis: true,
    render: (_: unknown, r: Approval) => nz(r.approverUserId),
    ...getColumnSearchProps<Approval>('ApRef', (value, record) =>
      textMatchesColumnFilter(value, record.approverUserId)
    ),
  },
  {
    title: 'URL',
    dataIndex: 'url',
    key: 'url',
    width: 140,
    ellipsis: true,
    render: (_: unknown, r: Approval) =>
      r.url ? (
        <a className="truncate max-w-[8rem] inline-block align-bottom" href={r.url || '#'} target="_blank" rel="noreferrer">
          link
        </a>
      ) : (
        '—'
      ),
  },
  {
    title: 'New URL',
    dataIndex: 'newUrl',
    key: 'newUrl',
    width: 120,
    ellipsis: true,
    render: (_: unknown, r: Approval) =>
      r.newUrl ? (
        <a className="truncate max-w-[8rem] inline-block align-bottom" href={r.newUrl || '#'} target="_blank" rel="noreferrer">
          link
        </a>
      ) : (
        '—'
      ),
  },
  {
    title: 'Temp URL',
    dataIndex: 'tempUrl',
    key: 'tempUrl',
    width: 120,
    ellipsis: true,
    render: (_: unknown, r: Approval) =>
      r.tempUrl ? (
        <a className="truncate max-w-[8rem] inline-block align-bottom" href={r.tempUrl || '#'} target="_blank" rel="noreferrer">
          link
        </a>
      ) : (
        '—'
      ),
  },

  {
    title: 'Created',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 118,
    render: (date: string) => (date ? dayjs(date).format('DD-MM-YYYY HH:mm') : '—'),
    sorter: (a: Approval, b: Approval) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
    ...getColumnSearchProps<Approval>('Created', (value, record) =>
      textMatchesColumnFilter(value, dayjs(record.createdAt).format('DD-MM-YYYY'))
    ),
  },
  {
    title: 'Updated',
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    width: 118,
    render: (date: string) => (date ? dayjs(date).format('DD-MM-YYYY HH:mm') : '—'),
    ...getColumnSearchProps<Approval>('Updated', (value, record) =>
      textMatchesColumnFilter(value, dayjs(record.updatedAt).format('DD-MM-YYYY'))
    ),
  },
]
